import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOMServer from "react-dom/server";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  useMap,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import "leaflet.heat";
import "leaflet/dist/leaflet.css";
import { FaExpand, FaCompress, FaMapMarkerAlt, FaFire } from "react-icons/fa";
import { PiMapPinSimpleFill } from "react-icons/pi";
import { useSelector } from "react-redux";
import { CommonContext } from "./../../context/commonContext";
import { goaBoundary } from "../../assets/goa.Boundary";
import DateRangePickerStyle from "../dateRangePicker/DateRangePickerStyle";
import { format } from "date-fns";

// HeatLayer Component
function HeatLayer({ points, options = {} }) {
  const map = useMap();
  const heatRef = useRef(null);

  const normalizedPoints = useMemo(() => {
    if (!points || points.length === 0) return [];
    const maxWeight = Math.max(...points.map((p) => p[2]), 1);
    const intensityFactor = 5;
    return points.map(([lat, lng, w]) => [lat, lng, (w / maxWeight) * intensityFactor]);
  }, [points]);

  useEffect(() => {
    if (!map) return;

    if (heatRef.current) {
      map.removeLayer(heatRef.current);
      heatRef.current = null;
    }

    if (normalizedPoints.length > 0) {
      heatRef.current = L.heatLayer(normalizedPoints, {
        radius: 30,
        blur: 0,
        maxZoom: 12,
        gradient: {
          0.5: "blue",
          0.4: "blue",
          0.6: "blue",
          0.8: "blue",
          1.0: "blue",
        },
        ...options,
      }).addTo(map);
    }

    return () => {
      if (heatRef.current) map.removeLayer(heatRef.current);
    };
  }, [map, normalizedPoints, options]);

  return null;
}

// Goa boundaries
const goaBounds = [
  [14.9, 73.6],
  [15.8, 74.3],
];

const outerBounds = [
  [
    [-90, -180],
    [-90, 180],
    [90, 180],
    [90, -180],
    [-90, -180],
  ],
];

const goaCoordinates = goaBoundary.features.map(
  (feature) => feature.geometry.coordinates[0]
);

// Mask layer to highlight Goa boundaries from outside
function MaskLayer() {
  const map = useMap();
  const themeMode = useSelector((state) => state.theme.mode);

  useEffect(() => {
    const mask = L.polygon(
      [
        outerBounds,
        ...goaCoordinates.map((coords) => coords.map(([lng, lat]) => [lat, lng])),
      ],
      {
        fillColor: themeMode ? "#000" : "#fff",
        fillOpacity: 1,
        stroke: false,
        interactive: false,
      }
    ).addTo(map);

    return () => {
      map.removeLayer(mask);
    };
  }, [map, themeMode]);

  return null;
}

// --- NEW COMPONENT: Goa Highlight Layer (Yellow-Red) ---
function GoaHighlightLayer() {
  const map = useMap();

  useEffect(() => {
    const highlightPoly = L.polygon(
      goaCoordinates.map((coords) => coords.map(([lng, lat]) => [lat, lng])),
      {
        fillColor: "url(#goaGradient)", // References the SVG gradient defined below
        fillOpacity: 1, // Transparency set to 0.5 so map details are still visible
        color: "red", // Red border
        weight: 1,
        interactive: false, // Ensures it doesn't block clicks on markers
      }
    ).addTo(map);

    return () => {
      map.removeLayer(highlightPoly);
    };
  }, [map]);

  return null;
}

// Fit map to Goa bounds
function FitGoaBounds() {
  const map = useMap();
  useEffect(() => {
    if (map) {
      map.fitBounds(goaBounds, { padding: [20, 20], maxZoom: 12 });
    }
  }, [map]);
  return null;
}

// Map control buttons
function MapControls({ fullSize, setFullSize, setShowMarkers, showHeat, setShowHeat }) {
  const map = useMap();
  const { setSidebarAct } = useContext(CommonContext);

  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 300);
  }, [fullSize, map]);

  return (
    <div className="absolute top-3 right-5 z-[500] flex flex-col gap-2">
      <button onClick={() => setShowMarkers((s) => !s)} title="Toggle Markers" className="p-2 rounded-md bg-white shadow hover:bg-slate-200 hidden">
        <FaMapMarkerAlt />
      </button>
      <button onClick={() => setShowHeat((s) => !s)} title="Toggle Heatmap" className="p-2 rounded-md bg-white shadow hover:bg-slate-200 hidden ">
        <FaFire />
      </button>
      <button onClick={() => { setSidebarAct(true); setFullSize((s) => !s); }} title="Toggle Fullscreen" className="p-2 rounded-md bg-white shadow hover:bg-slate-200">
        {fullSize ? <FaCompress /> : <FaExpand />}
      </button>
    </div>
  );
}

function createPinIcon(color = "#3b82f6") {
  const iconHtml = ReactDOMServer.renderToStaticMarkup(
    <div style={{ fontSize: "16px", color }}>
      <PiMapPinSimpleFill />
    </div>
  );

  return L.divIcon({
    className: "",
    html: iconHtml,
    iconSize: [16, 16],
    iconAnchor: [8, 16], 
  });
}

// Main Component
export default function GoaHeatmap({ 
  datasets, 
  defaultTimeKey, 
  title, 
  selectDate, 
  setSelectDate,
  initialDateRange,
  disableInitialDateCall 
}) {
  const [timeKey, setTimeKey] = useState(defaultTimeKey || Object.keys(datasets)[0] || format(new Date(), "yyyy-MM-dd"));
  const [showMarkers, setShowMarkers] = useState(true);
  const [fullSize, setFullSize] = useState(false);
  const [showHeat, setShowHeat] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [popupKey, setPopupKey] = useState(0); 
  const [localDateRange, setLocalDateRange] = useState(initialDateRange);
  
  const lastApiCallRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!selectDate) return;
    let date = selectDate.date || selectDate.startDate;
    if (!date) return;
    const formatted = format(new Date(date), "yyyy-MM-dd");
    if (Object.keys(datasets).includes(formatted)) setTimeKey(formatted);
    else setTimeKey(defaultTimeKey || Object.keys(datasets)[0]);
    
    setSelectedPoint(null);
    setPopupKey(prev => prev + 1); 
  }, [selectDate, datasets, defaultTimeKey]);

  useEffect(() => {
    if (initialDateRange && 
        (initialDateRange.startDate !== localDateRange?.startDate || 
         initialDateRange.endDate !== localDateRange?.endDate)) {
      setLocalDateRange(initialDateRange);
      lastApiCallRef.current = {
        startDate: initialDateRange.startDate,
        endDate: initialDateRange.endDate
      };
    }
  }, [initialDateRange]);

  const handleDateChange = useCallback((val) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const isPartialSelection = val.startDate === val.endDate;
    
    const isDifferentFromLastCall = 
      !lastApiCallRef.current ||
      lastApiCallRef.current.startDate !== val.startDate ||
      lastApiCallRef.current.endDate !== val.endDate;
    
    const delay = isPartialSelection ? 500 : 100;
    setLocalDateRange(val);
    
    if (isDifferentFromLastCall) {
      debounceTimerRef.current = setTimeout(() => {
        lastApiCallRef.current = {
          startDate: val.startDate,
          endDate: val.endDate
        };
        if (setSelectDate) setSelectDate(val);
      }, delay);
    }
  }, [setSelectDate]);

  const currentPoints = useMemo(() => {
    return (datasets[timeKey] || [])
      .filter((p) => p.lat >= 14.9 && p.lat <= 15.8 && p.lng >= 73.6 && p.lng <= 74.3)
      .map((p) => [p.lat, p.lng, p.weight || 1]);
  }, [datasets, timeKey]);

  const currentItems = useMemo(() => {
    return (datasets[timeKey] || []).filter(
      (p) => p.lat >= 14.9 && p.lat <= 15.8 && p.lng >= 73.6 && p.lng <= 74.3
    );
  }, [datasets, timeKey]);

  const handleMarkerClick = (point) => {
    if (selectedPoint && selectedPoint.id === point.id) setSelectedPoint(null);
    else {
      setSelectedPoint(point);
      setPopupKey(prev => prev + 1); 
    }
  };

  const handlePopupClose = () => setSelectedPoint(null);

  return (
    <div className="relative">
      {/* SVG Gradient Definition required for the Goa Highlight */}
      <svg style={{ width: 0, height: 0, position: "absolute" }} aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="goaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fde047" stopOpacity="0.8" />   {/* Yellow */}
            <stop offset="50%" stopColor="#ea580c" stopOpacity="0.8" />   {/* Orange */}
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0.8" />  {/* Red */}
          </linearGradient>
        </defs>
      </svg>

      <div className={`${fullSize ? "fixed inset-0 z-50" : "w-full h-[86vh] rounded-2xl border"} overflow-hidden`}>
        <div className={`h-full flex flex-col ${fullSize ? "" : "rounded-2xl"} bg-white dark:bg-slate-900/80`}>
          <div className="flex-1 overflow-hidden relative">
            {!datasets[timeKey] || (currentPoints.length === 0 && (
              <div className="absolute top-1/2 left-1/2 z-[500] bg-white p-3 rounded-md shadow transform -translate-x-1/2 -translate-y-1/2">
                No data available for{" "} {selectDate ? `${selectDate.startDate} To ${selectDate.endDate}` : timeKey}
              </div>
            ))}

            <div className="absolute top-5 z-[500] left-24" style={{ width: "fit-content" }}>
              <DateRangePickerStyle
                mode="range"
                onDateChange={handleDateChange}
                initialRange={initialDateRange}
                wrapperWidth="w-[190px]"
                position_box="left-0"
                defaultRangeType="today"  
                disableInitialDateCall={disableInitialDateCall}
              />
            </div>

            <MapContainer
              style={{ height: "100%", width: "100%", "--leaflet-control-right": "20px" }}
              bounds={goaBounds}
              maxBounds={goaBounds}
              maxBoundsViscosity={1.0}
              scrollWheelZoom={true}
              minZoom={9.5}
              maxZoom={20}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
              
              {/* Layers Ordering */}
              <MaskLayer /> 
              <GoaHighlightLayer /> {/* The new yellow-red layer added here */}
              <FitGoaBounds />
              
              {showHeat && currentPoints.length > 0 && <HeatLayer points={currentPoints} />}
              {showMarkers &&
                currentItems.map((point) => {
                  const color = "#1b107aff";
                  return (
                    <Marker
                      key={point.id}
                      position={[point.lat, point.lng]}
                      icon={createPinIcon(color)}
                      eventHandlers={{ click: () => handleMarkerClick(point) }}
                    >
                      <Tooltip>
                        <div className="text-xs">
                          <div className="font-semibold">{point.zone}</div>
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}

              {/* Popup */}
              {selectedPoint && (
                <Popup
                  key={`popup-${popupKey}`}
                  position={[selectedPoint.lat, selectedPoint.lng]}
                  onClose={handlePopupClose}
                  closeButton={true}
                  autoClose={false}
                  closeOnClick={false}
                >
                  <div className="p-2 min-w-[200px]">
                    <div className="font-semibold text-gray-800 dark:text-white mb-3">{selectedPoint.zone}</div>
                    
                    {selectedPoint.booking_ids && selectedPoint.booking_ids.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedPoint.booking_ids.map((id, index) => (
                          <div key={index} className="flex items-start">
                            <div className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[80px]">Booking ID:</div>
                            <div className="text-sm text-gray-800 dark:text-gray-300 ml-2 break-all">{id}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-start">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[80px]">Booking ID:</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 ml-2 italic">No booking IDs</div>
                      </div>
                    )}
                  </div>
                </Popup>
              )}
              <MapControls
                fullSize={fullSize}
                setFullSize={setFullSize}
                setShowMarkers={setShowMarkers}
                showHeat={showHeat}
                setShowHeat={setShowHeat}
              />
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
}